/**
 * Every tunable budget in one place. Values are provisional until profiled
 * against a synthetic 1000-test pack (scripts/make-fixtures.mjs) — measured,
 * not sacred.
 */
var MiB = 1024 * 1024;

/** Ceiling for the whole-body cache when a server ignores Range requests. */
export var MAX_WHOLE_BODY_BYTES = 256 * MiB;

/** Refuse to read any single entry larger than this (checked against the
 * central directory BEFORE fetching a byte). */
export var MAX_ENTRY_BYTES = 128 * MiB;

/** Byte budget for decompressed artifact bytes (screenshots, logs). */
export var ARTIFACT_CACHE_BYTES = 64 * MiB;

/** Budgets for object URLs handed to <img>/<video>. */
export var OBJECT_URL_CACHE_BYTES = 64 * MiB;
export var OBJECT_URL_CACHE_COUNT = 100;

/** Hydration concurrency. Blob (local File) reads are memcpy-cheap. For HTTP,
 * remote object stores (Azure) serve over HTTP/2 — which multiplexes many
 * streams over one connection, not the old HTTP/1.1 6-per-origin limit — and
 * throttle PER-STREAM, so more concurrent ranged reads raise aggregate
 * throughput (measured ~3x vs a single stream, up to the origin's aggregate
 * ceiling). Bounded to keep the in-flight set sane. */
export var HYDRATE_CONCURRENCY_HTTP = 12;
export var HYDRATE_CONCURRENCY_BLOB = 12;

/** Yield to the event loop when parse work exceeds this budget in a frame. */
export var YIELD_BUDGET_MS = 8;

/** Yield while parsing huge central directories, every N entries. */
export var CD_PARSE_YIELD_EVERY = 2500;

/** Loader cap on failure-index rows read from failure.yaml (each row may
 * cost one ~1 KB ranged read for its per-step detail file). Feeds BOTH the
 * report widget and the Issues tab — the Issues tab wants them all; 100
 * failures in one pack is already beyond any sane run. */
export var MAX_ATTENTION_ITEMS = 100;

/** The report "Need your attention" widget renders at most this many rows
 * (preserves its pre-Issues-tab height now the loader cap is higher). */
export var ATTENTION_DISPLAY_ITEMS = 20;

/** Detail view: refuse to load an NDJSON log entry bigger than this (checked
 * against the central directory before fetching a byte — the drawer shows an
 * honest "too large" state instead). */
export var MAX_LOG_TEXT_BYTES = 32 * MiB;

/** Detail view: cap for auteur/execution.json (sample packs: ~93 KB). */
export var MAX_EXECUTION_JSON_BYTES = 16 * MiB;

/** Detail view: cap per v16-trajectory <n>-run_summary.json (sample packs:
 * 2–16 KB each; the embedded steps array grows with the objective). */
export var MAX_TRAJECTORY_SUMMARY_BYTES = 1 * MiB;

/** Screenshot prefetch window around the selected step. */
export var SCREENSHOT_PREFETCH_AHEAD = 2;
export var SCREENSHOT_PREFETCH_BEHIND = 1;

/** step.json reads per test are pooled — a 500-step test must not fire
 * hundreds of concurrent ranged reads. Over HTTP/2 (per-stream throttle) a
 * larger pool reads the many small step.json files in fewer waves; these are
 * latency-bound (~sub-KB each), so they don't compete for the byte ceiling. */
export var STEPJSON_READ_CONCURRENCY = 12;

/* eslint-disable no-restricted-globals */
// Typed non-optional: a truly absent `process` throws a ReferenceError at
// runtime, which the try/catch below absorbs — while plain member access
// keeps CRA's DefinePlugin substitution working (see ghEnv()).

/**
 * Env reader for the gh-artifact/OAuth knobs. CRA's DefinePlugin replaces
 * the literal `process.env` member expression at APP build time (this
 * package ships babel-compiled with it intact); Jest under node reads the
 * real env. PLAIN member access only — optional chaining compiles into a
 * shape DefinePlugin cannot substitute, which silently falls through to
 * production defaults in dev (cost a live debug once).
 */
function ghEnv() {
  try {
    return {
      apiBase: process.env.REACT_APP_EVIDENCE_GH_API_BASE,
      clientId: process.env.REACT_APP_EVIDENCE_GH_CLIENT_ID,
      exchangeUrl: process.env.REACT_APP_EVIDENCE_GH_EXCHANGE_URL,
      nodeEnv: process.env.NODE_ENV
    };
  } catch (_unused) {
    return {};
  }
}
var GH_ENV = ghEnv();

/**
 * GitHub API base for artifact downloads — the viewer calls it directly
 * with the USER'S OAuth token (Connect GitHub flow; api.github.com is
 * CORS-open for Authorization-header requests). The env override exists
 * for the dev fixture shim (`/gh-api`); deliberately NOT overridable from
 * the page URL.
 */
export var GH_API_BASE = (GH_ENV.apiBase || 'https://api.github.com').replace(/\/$/, '');

/** OAuth app client id (public, baked into the bundle at app build).
 * Empty string ⇒ the gateway hides the Connect button and offers only the
 * download-and-drop fallback. */
export var GH_OAUTH_CLIENT_ID = GH_ENV.clientId || '';

/** OAuth scope. `repo` is required to read PRIVATE repos' artifacts (user
 * decision 2026-09-11: private works day one; classic scopes are coarse —
 * the consent screen words it as full private-repo control). */
export var GH_OAUTH_SCOPE = 'repo';
export var GH_AUTHORIZE_URL = 'https://github.com/login/oauth/authorize';

/**
 * The server-side code→token exchange (GitHub's token endpoint has no CORS
 * and needs the client secret). Precedence: build-time env override (set
 * per environment — stage vs prod backend route), else the dev same-origin
 * shim, else the prod default. Deliberately NOT overridable from the page
 * URL — a URL-supplied endpoint could exfiltrate OAuth codes; a build-time
 * env var is safe.
 */
export var GH_OAUTH_EXCHANGE_URL = GH_ENV.exchangeUrl || (GH_ENV.nodeEnv && GH_ENV.nodeEnv !== 'production' ? '/gh-oauth/exchange' : 'https://evidence-gh-oauth.lambdatest.com/exchange');

/**
 * Deep-link bases for the detail header CTAs. The standalone viewer
 * (evidence.lambdatest.com) needs ABSOLUTE origins; embedded-in-TMS could use
 * same-origin, but one config keeps both paths uniform. Build-time env
 * override wins; otherwise the page host decides stage vs prod at RUNTIME
 * (isStageHost below). PLAIN process.env member access (DefinePlugin) — see ghEnv().
 */
function deepLinkEnv() {
  try {
    return {
      tmsClient: process.env.REACT_APP_EVIDENCE_TMS_CLIENT,
      hyperexecute: process.env.REACT_APP_EVIDENCE_HYPEREXECUTE_URL
    };
  } catch (_unused2) {
    return {};
  }
}
var DEEP_LINK_ENV = deepLinkEnv();

// Stage vs prod is detected from the page host at RUNTIME, so one build is
// correct on both (embedded-in-TMS or standalone): stage/dev hosts end in
// lambdatestinternal.com, start with stage-, or are localhost.
function isStageHost() {
  try {
    var h = window.location.hostname;
    return /lambdatestinternal\.com$/i.test(h) || h.startsWith("stage-") || h === "localhost" || h === "127.0.0.1";
  } catch (_unused3) {
    return false;
  }
}

/** TMS web-client origin. The Test Case ID links to
 * {base}/projects/{projectId}/test-cases/{testCaseId}/dashboard — TMS handles
 * the login → redirect-back when the viewer is opened signed-out. */
export var TMS_CLIENT_BASE = (DEEP_LINK_ENV.tmsClient || (isStageHost() ? 'https://stage-test-manager.lambdatestinternal.com' : 'https://test-manager.lambdatest.com')).replace(/\/$/, '');

/** HyperExecute origin. "View on HyperExecute" links to
 * {base}/hyperexecute/task?jobId={hyper_job_id}. */
export var HYPEREXECUTE_BASE = (DEEP_LINK_ENV.hyperexecute || (isStageHost() ? 'https://stage-hyperexecute.lambdatestinternal.com' : 'https://hyperexecute.lambdatest.com')).replace(/\/$/, '');